import type { OrgMemberListItem } from "@solomon-apps/shared";
import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { useInvitationExpiry } from "@solomon-apps/shared/src/hooks/useInvitationExpiry";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import React from "react";

import { SortableColHead } from "../../Positions/components/SortableColHead";
import {
    displayName,
    firstName,
    formatAdded,
    formatLastActive,
    memberStatusLabel,
    memberStatusTone,
    STATUS_TONE_TEXT,
    type TeamSort,
    type TeamSortColumn
} from "../teamData";

/**
 * One column template for the header row, every body row and the skeleton,
 * so the columns line up, one line per member:
 * Status · Name · Email · Role · Last Active · Added.
 * Actions live on the member page (click the row).
 *
 * Responsive via CONTAINER queries, the Positions table's method
 * (`@container/teamtable` on the table wrapper, so columns drop by the
 * table's own width, not the viewport's). As the table narrows, things drop
 * in this order: the Status label collapses to its dot (below @6xl), Name
 * shows the first name only (below @5xl), then Last Active (below @4xl),
 * Added (below @3xl), Email (below @2xl) and Role (below @xl) drop out. Name
 * always stays. Each breakpoint lists only the tracks for what is visible at
 * that width; the matching header and row cells carry the same
 * `hidden @Nxl/teamtable:*` toggle, and a `display:none` cell drops out of
 * the grid, so the remaining cells fill the listed tracks in order. The
 * Status track is a 16px dot until @6xl, where it widens to fit the label.
 *
 * why: the fractional per-breakpoint templates have no Tailwind default.
 */
const ROW_GRID =
    // base (<@xl): status-dot · name
    "grid w-full items-center gap-4 grid-cols-[16px_minmax(120px,1fr)] " +
    // +role
    "@xl/teamtable:grid-cols-[16px_minmax(120px,1.6fr)_minmax(72px,0.9fr)] " +
    // +email
    "@2xl/teamtable:grid-cols-[16px_minmax(120px,1.6fr)_minmax(160px,2fr)_minmax(72px,0.9fr)] " +
    // +added
    "@3xl/teamtable:grid-cols-[16px_minmax(120px,1.6fr)_minmax(160px,2fr)_minmax(72px,0.9fr)_minmax(96px,1fr)] " +
    // +last active
    "@4xl/teamtable:grid-cols-[16px_minmax(120px,1.6fr)_minmax(160px,2fr)_minmax(72px,0.9fr)_minmax(120px,1.3fr)_minmax(96px,1fr)] " +
    // (full name from @5xl: same tracks) +status label: the first track
    // widens to fit the dot and the text
    "@6xl/teamtable:grid-cols-[minmax(96px,0.8fr)_minmax(120px,1.6fr)_minmax(160px,2fr)_minmax(72px,0.9fr)_minmax(120px,1.3fr)_minmax(96px,1fr)]";

/** Per-column visibility, shared by header, rows and skeleton. */
const SHOW = {
    statusLabel: "hidden @6xl/teamtable:block",
    fullName: "hidden @5xl/teamtable:inline",
    firstName: "@5xl/teamtable:hidden",
    lastActive: "hidden @4xl/teamtable:block",
    added: "hidden @3xl/teamtable:block",
    email: "hidden @2xl/teamtable:block",
    role: "hidden @xl/teamtable:block",
    // The sortable headers are flex buttons, so they show as flex.
    roleHead: "hidden @xl/teamtable:flex",
    lastActiveHead: "hidden @4xl/teamtable:flex"
} as const;

type SortableColumn = Exclude<TeamSortColumn, "default">;

export interface MemberRow {
    member: OrgMemberListItem;
    isSelf: boolean;
}

interface MembersTableProps {
    rows: MemberRow[];
    sort: TeamSort;
    onSort: (column: SortableColumn) => void;
    /** Opens the member's page (`/team/:memberUuid`). */
    onOpen: (uuid: string) => void;
    /** Shown in place of the rows when there are none. */
    empty: React.ReactNode;
}

/**
 * The Team roster as a table. Rows are DS-style `ListItem`s (the Ember
 * top-line wipe on hover) on the shared column template, the same
 * construction as `/reward-destinations` and `/positions`. Clicking a row, or
 * activating its name, opens the member's page, which holds every action on
 * the member. Every text in the table, header labels included, is set in
 * `fine-print`.
 */
export function MembersTable({
    rows,
    sort,
    onSort,
    onOpen,
    empty
}: MembersTableProps): React.ReactElement {
    // SortableColHead lights the active column; the default exception-first
    // order is no column, so nothing lights.
    const activeColumn = sort.column === "default" ? undefined : sort.column;

    return (
        <section
            aria-label="Team members"
            className="@container/teamtable flex flex-col"
        >
            <MembersTableHeader
                activeColumn={activeColumn}
                direction={sort.direction}
                onSort={onSort}
            />
            <List
                // why: ListItem draws a top AND bottom border inline (rows
                // collapse onto one line via -1px). The last row's bottom is
                // a closing terminal rule (ui-guide 5g), and only `!` beats
                // an inline style.
                className="[&>*:last-child]:!border-b-0"
                state={rows.length === 0 ? "empty" : "populated"}
                empty={empty}
            >
                {rows.map((row) => (
                    <MemberTableRow
                        key={row.member.uuid}
                        row={row}
                        onOpen={onOpen}
                    />
                ))}
            </List>
        </section>
    );
}

function MembersTableHeader({
    activeColumn,
    direction,
    onSort
}: {
    activeColumn: SortableColumn | undefined;
    direction: TeamSort["direction"];
    onSort: (column: SortableColumn) => void;
}): React.ReactElement {
    const head = (
        label: string,
        column: SortableColumn,
        className?: string,
        align?: "left" | "right"
    ): React.ReactElement => (
        <SortableColHead<SortableColumn | "none">
            label={label}
            columnKey={column}
            activeColumn={activeColumn ?? "none"}
            direction={direction}
            onSort={(key) => {
                if (key !== "none") onSort(key);
            }}
            variant="fine-print"
            align={align}
            className={className}
        />
    );
    return (
        // The column-label row is not a data row. Its one rule is the first
        // row's own top line, collapsed onto the label row's bottom edge, so
        // the label row draws none (two here would stack a double line).
        <div className={cn(ROW_GRID, "px-4 md:px-6 pt-4 pb-2")}>
            <span className="flex min-w-0 items-center">
                <ColumnLabel className={SHOW.statusLabel}>Status</ColumnLabel>
            </span>
            {head("Name", "name")}
            <ColumnLabel className={SHOW.email}>Email</ColumnLabel>
            {head("Role", "role", SHOW.roleHead)}
            {head("Last Active", "lastActive", SHOW.lastActiveHead, "right")}
            <ColumnLabel className={cn(SHOW.added, "text-right")}>
                Added
            </ColumnLabel>
        </div>
    );
}

function ColumnLabel({
    children,
    className
}: {
    children: React.ReactNode;
    className?: string;
}): React.ReactElement {
    return (
        <Text
            variant="fine-print"
            render={<span />}
            className={cn("truncate text-foreground-muted", className)}
        >
            {children}
        </Text>
    );
}

function MemberTableRow({
    row,
    onOpen
}: {
    row: MemberRow;
    onOpen: (uuid: string) => void;
}): React.ReactElement {
    const { member, isSelf } = row;
    // Re-renders when an invite lapses, so status and expiry stay current.
    const { now } = useInvitationExpiry(
        member.status === "Invited" ? member.inviteExpiresAt : null
    );
    const tone = memberStatusTone(member, now);
    const statusLabel = memberStatusLabel(member, now);

    return (
        <ListItem
            itemKey={member.uuid}
            padding="px-4 md:px-6 py-3"
            gap="gap-0"
            borderColor="rgb(var(--color-line-base))"
            onClick={() => onOpen(member.uuid)}
        >
            <div className={ROW_GRID}>
                {/* Status: text with a dot, never a pill (ui-guide 5f); the
                    label shows only when the table is wide enough. Below
                    that the dot carries it, named for assistive tech. */}
                <span className="flex min-w-0 items-center gap-1.5">
                    <span
                        className="shrink-0"
                        role="img"
                        aria-label={statusLabel}
                    >
                        <IndicationUnit
                            variant="status"
                            state={tone}
                            size={7}
                        />
                    </span>
                    <Text
                        variant="fine-print"
                        render={<span />}
                        aria-hidden="true"
                        className={cn(
                            SHOW.statusLabel,
                            "truncate",
                            STATUS_TONE_TEXT[tone]
                        )}
                    >
                        {statusLabel}
                    </Text>
                </span>

                {/* Name: the row's keyboard handle. It has no handler of its
                    own: its click (or Enter/Space) bubbles to the row, which
                    opens the member page. The full name, or the first name
                    in a narrow table. */}
                <button
                    type="button"
                    className="flex min-w-0 items-baseline gap-2 text-left"
                    aria-label={`${displayName(member)}${isSelf ? " (You)" : ""}`}
                >
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="min-w-0 truncate text-foreground-base"
                    >
                        <span className={SHOW.fullName}>
                            {displayName(member)}
                        </span>
                        <span className={SHOW.firstName}>
                            {firstName(member)}
                        </span>
                    </Text>
                    {isSelf && (
                        <Text
                            variant="fine-print"
                            render={<span />}
                            className="shrink-0 text-foreground-muted"
                        >
                            (You)
                        </Text>
                    )}
                </button>

                <Cell className={cn(SHOW.email, "text-foreground-muted")}>
                    {member.email}
                </Cell>
                <Cell className={SHOW.role}>{member.orgRole}</Cell>
                {/* Dates stay in the row's body face (ABC Social), like
                    every other cell, right-aligned. */}
                <Cell className={cn(SHOW.lastActive, "text-right")}>
                    {formatLastActive(member, now)}
                </Cell>
                <Cell className={cn(SHOW.added, "text-right")}>
                    {formatAdded(member.createdAt)}
                </Cell>
            </div>
        </ListItem>
    );
}

function Cell({
    children,
    className
}: {
    children: React.ReactNode;
    className?: string;
}): React.ReactElement {
    return (
        <Text
            variant="fine-print"
            render={<span />}
            className={cn("min-w-0 truncate text-foreground-base", className)}
        >
            {children}
        </Text>
    );
}

/** First-load placeholder on the table's own template (ui-guide 5j). */
export function MembersTableSkeleton(): React.ReactElement {
    return (
        <section
            aria-label="Loading team members"
            aria-busy="true"
            className="@container/teamtable flex flex-col"
        >
            <div className={cn(ROW_GRID, "px-4 md:px-6 pt-4 pb-2")}>
                <span className="flex min-w-0 items-center">
                    <Skeleton
                        className={cn(
                            SHOW.statusLabel,
                            "h-4 w-12 rounded-none"
                        )}
                    />
                </span>
                <Skeleton className="h-4 w-12 rounded-none" />
                <Skeleton className={cn(SHOW.email, "h-4 w-12 rounded-none")} />
                <Skeleton className={cn(SHOW.role, "h-4 w-10 rounded-none")} />
                <Skeleton
                    className={cn(
                        SHOW.lastActive,
                        "h-4 w-20 justify-self-end rounded-none"
                    )}
                />
                <Skeleton
                    className={cn(
                        SHOW.added,
                        "h-4 w-12 justify-self-end rounded-none"
                    )}
                />
            </div>
            {Array.from({ length: 4 }).map((_, index) => (
                <div
                    key={index}
                    // One line per row on its top edge, as ListItem; the
                    // first one is the rule under the column labels.
                    className="border-t border-line-base px-4 md:px-6 py-3"
                >
                    <div className={ROW_GRID}>
                        <Skeleton className="size-2 rounded-none" />
                        <Skeleton className="h-4 w-36 max-w-full rounded-none" />
                        <Skeleton
                            className={cn(
                                SHOW.email,
                                "h-4 w-48 max-w-full rounded-none"
                            )}
                        />
                        <Skeleton
                            className={cn(SHOW.role, "h-4 w-14 rounded-none")}
                        />
                        <Skeleton
                            className={cn(
                                SHOW.lastActive,
                                "h-4 w-20 justify-self-end rounded-none"
                            )}
                        />
                        <Skeleton
                            className={cn(
                                SHOW.added,
                                "h-4 w-20 justify-self-end rounded-none"
                            )}
                        />
                    </div>
                </div>
            ))}
        </section>
    );
}
